import React, { useEffect, useState } from 'react';
import api from '../api';
import EvidencePanel from '../components/EvidencePanel';
import WorkflowPanel from '../components/WorkflowPanel';
import DrillDownModal from '../components/DrillDownModal';
import { Factory, Lightning, Drop, Recycle, ShoppingCart, Shield, FileText, Warning, Plus, X, Robot, SpinnerGap, CheckCircle, FunnelSimple, Coin, MagnifyingGlass } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

const TABS = [
  { key: 'utilities', label: 'Utilities & Waste', icon: Lightning },
  { key: 'procurement', label: 'Procurement', icon: ShoppingCart },
  { key: 'governance', label: 'Resilience', icon: Shield },
  { key: 'reporting', label: 'Disclosure', icon: FileText },
  { key: 'currency', label: 'Currency Ops', icon: Coin },
];

const STATUS_STYLES = {
  'On Track': 'bg-status-on-track-bg text-status-on-track',
  'Watch': 'bg-status-watch-bg text-status-watch',
  'Alert': 'bg-status-alert-bg text-status-alert',
  'Submitted': 'bg-blue-50 text-blue-600',
  'Approved': 'bg-status-on-track-bg text-status-on-track',
  'Rework': 'bg-status-alert-bg text-status-alert',
  'Open': 'bg-status-watch-bg text-status-watch',
  'Overdue': 'bg-status-alert-bg text-status-alert',
  'In Progress': 'bg-blue-50 text-blue-600',
  'Awarded': 'bg-status-on-track-bg text-status-on-track',
  'Evaluation': 'bg-blue-50 text-blue-600',
  'For Clarification': 'bg-status-watch-bg text-status-watch',
  'Completed': 'bg-status-on-track-bg text-status-on-track',
  'Complete': 'bg-status-on-track-bg text-status-on-track',
  'Partial': 'bg-status-watch-bg text-status-watch',
  'Pending': 'bg-surface-muted text-content-disabled',
};
function SB({ status }) { return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_STYLES[status] || 'bg-surface-muted text-content-secondary'}`}>{status}</span>; }

export default function OwnOperations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('utilities');
  const [selSite, setSelSite] = useState('all');
  const [selMetric, setSelMetric] = useState('FAC-001');
  const [showEntry, setShowEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({ site_id: '', period: '', value: '', commentary: '' });
  const [entryError, setEntryError] = useState('');
  const [entryOk, setEntryOk] = useState('');
  const [selRow, setSelRow] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [drillKey, setDrillKey] = useState(null);

  const fetchData = () => { api.get('/api/dashboard/operations').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { fetchData(); }, []);

  const metricLabels = { 'FAC-001': 'Electricity (kWh)', 'FAC-002': 'Water (m3)', 'FAC-003': 'Waste (kg)' };

  const handleEntry = async (e) => {
    e.preventDefault(); setEntryError('');
    try {
      await api.post('/api/metric-values/entry', { ...entryForm, metric_id: selMetric });
      setEntryOk('Submitted'); setShowEntry(false); fetchData(); setTimeout(() => setEntryOk(''), 3000);
    } catch (err) { setEntryError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed'); }
  };

  const runAI = async () => {
    setAiLoading(true); setAiResult(null);
    try {
      const sid = selSite !== 'all' ? selSite : 'BSP-HO-MNL';
      const { data: r } = await api.post('/api/ai/anomaly-detection', { site_id: sid, metric_id: selMetric });
      setAiResult(r);
    } catch { setAiResult({ analysis: { summary: 'Analysis failed' } }); }
    finally { setAiLoading(false); }
  };

  if (loading) return <div className="animate-pulse text-content-secondary">Loading...</div>;
  if (!data) return <div className="text-status-alert">Failed to load</div>;

  const { sites, metric_values, procurement, suppliers, governance_records, governance_actions, submissions, cycles, currency_metrics } = data;

  const filtered = metric_values.filter(v => (selSite === 'all' || v.site_id === selSite) && v.metric_id === selMetric);
  const trendData = [...filtered].sort((a, b) => (a.period || '').localeCompare(b.period || '')).map(v => ({ period: v.period, value: v.value }));

  return (
    <div data-testid="own-operations" className="space-y-5 animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-light text-3xl text-content-primary tracking-tight">Own Operations Dashboard</h1>
          <p className="text-sm text-content-secondary mt-1">Site scorecards, utilities, procurement, resilience & disclosure</p>
        </div>
      </div>

      {/* Site Scorecards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {sites.map((s, i) => (
          <div key={i} data-testid={`site-card-${i}`} className="bg-white border border-border-default rounded-lg p-2.5 hover:-translate-y-0.5 hover:shadow-sm transition-all group">
            <div className="flex items-start justify-between">
              <p className="text-[10px] font-semibold text-content-primary truncate flex-1">{s.name}</p>
              <button data-testid={`site-drill-${i}`} onClick={() => setDrillKey(`ops_site_${s.site_id}`)} className="w-5 h-5 rounded flex items-center justify-center text-content-disabled hover:text-brand-accent hover:bg-brand-accent/10 transition-all opacity-0 group-hover:opacity-100 flex-shrink-0 ml-1" title="Drill down">
                <MagnifyingGlass size={10} />
              </button>
            </div>
            <div className="mt-1.5 space-y-1 cursor-pointer" onClick={() => { setSelSite(s.site_id); setTab('utilities'); }}>
              <div className="flex items-center justify-between"><span className="text-[8px] text-content-disabled">Elec</span><span className={`text-[10px] font-mono font-semibold ${(s.electricity_change_pct || 0) <= 0 ? 'text-status-on-track' : 'text-status-alert'}`}>{s.electricity_change_pct > 0 ? '+' : ''}{s.electricity_change_pct}%</span></div>
              <div className="flex items-center justify-between"><span className="text-[8px] text-content-disabled">Water</span><span className={`text-[10px] font-mono font-semibold ${(s.water_change_pct || 0) <= 0 ? 'text-status-on-track' : 'text-status-alert'}`}>{s.water_change_pct > 0 ? '+' : ''}{s.water_change_pct}%</span></div>
              <div className="flex items-center justify-between"><span className="text-[8px] text-content-disabled">Resil</span><span className="text-[10px] font-mono font-semibold text-content-primary">{s.resilience_score}</span></div>
            </div>
          </div>
        ))}
      </div>

      {entryOk && <div className="flex items-center gap-2 p-2.5 bg-status-on-track-bg text-status-on-track text-xs rounded-lg"><CheckCircle size={14} weight="fill" />{entryOk}</div>}

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-muted p-1 rounded-lg w-fit overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => { setTab(t.key); setSelRow(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap ${tab === t.key ? 'bg-brand-primary text-white shadow-sm' : 'text-content-secondary hover:bg-white'}`}>
            <t.icon size={13} />{t.label}
          </button>
        ))}
      </div>

      {/* UTILITIES TAB */}
      {tab === 'utilities' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <FunnelSimple size={14} className="text-content-disabled" />
            <select data-testid="ops-site-filter" value={selSite} onChange={e => setSelSite(e.target.value)} className="border border-border-default rounded-lg px-2.5 py-1.5 text-xs bg-white">
              <option value="all">All Sites</option>
              {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
            </select>
            <div className="flex gap-1 bg-surface-muted p-0.5 rounded-lg">
              {Object.entries(metricLabels).map(([k, v]) => (
                <button key={k} data-testid={`mf-${k}`} onClick={() => setSelMetric(k)} className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${selMetric === k ? 'bg-brand-primary text-white' : 'text-content-secondary hover:bg-white'}`}>{v}</button>
              ))}
            </div>
            <button data-testid="ai-btn" onClick={runAI} disabled={aiLoading} className="flex items-center gap-1 px-3 py-1.5 bg-brand-accent text-white rounded-lg text-[10px] font-semibold hover:opacity-90 disabled:opacity-50">
              {aiLoading ? <SpinnerGap size={12} className="animate-spin" /> : <Robot size={12} />}{aiLoading ? 'Analyzing...' : 'AI Analysis'}
            </button>
            <button data-testid="new-entry-btn" onClick={() => setShowEntry(true)} className="flex items-center gap-1 px-3 py-1.5 bg-brand-primary text-white rounded-lg text-[10px] font-semibold hover:bg-brand-primary-hover"><Plus size={12} />New Entry</button>
          </div>

          {aiResult && (
            <div data-testid="ai-panel" className="bg-white border-2 border-brand-accent/30 rounded-lg p-4 animate-slide-up">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-heading font-semibold text-xs text-content-primary flex items-center gap-1.5"><Robot size={14} className="text-brand-accent" />AI Analysis</h4>
                <button onClick={() => setAiResult(null)}><X size={12} /></button>
              </div>
              <p className="text-xs text-content-primary">{aiResult.analysis?.summary}</p>
              {aiResult.analysis?.predictions?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {aiResult.analysis.predictions.map((p, i) => (
                    <div key={i} className="p-2 bg-surface-default rounded-lg text-center">
                      <p className="text-[9px] font-mono text-brand-primary">{p.period}</p>
                      <p className="text-sm font-heading font-bold">{typeof p.predicted_value === 'number' ? p.predicted_value.toLocaleString() : p.predicted_value}</p>
                    </div>
                  ))}
                </div>
              )}
              {aiResult.analysis?.recommendations?.length > 0 && (
                <ul className="mt-2 space-y-1">{aiResult.analysis.recommendations.map((r, i) => <li key={i} className="text-[10px] text-content-secondary flex items-start gap-1"><CheckCircle size={10} className="text-brand-primary mt-0.5 flex-shrink-0" />{r}</li>)}</ul>
              )}
            </div>
          )}

          {trendData.length > 0 && (
            <div className="bg-white border border-border-default rounded-lg p-4">
              <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">{metricLabels[selMetric]} Trend</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={v => [v.toLocaleString()]} />
                  <Line type="monotone" dataKey="value" stroke="#0A4D3C" strokeWidth={2} dot={{ fill: '#0A4D3C', r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white border border-border-default rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table data-testid="util-table" className="w-full text-[12px]">
                <thead><tr className="bg-surface-muted">
                  {['Period', 'Site', 'Value', 'Baseline', 'Status', 'Commentary', ''].map(h => <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-border-subtle">
                  {filtered.slice(0, 20).map((v, i) => (
                    <tr key={i} className={`hover:bg-surface-muted/50 cursor-pointer ${selRow?.id === v.id ? 'bg-brand-primary/5' : ''}`} onClick={() => setSelRow(selRow?.id === v.id ? null : v)}>
                      <td className="px-4 py-2 font-mono text-[10px]">{v.period}</td>
                      <td className="px-4 py-2 font-medium">{v.site_name || v.site_id}</td>
                      <td className="px-4 py-2 font-mono font-medium text-right">{v.value?.toLocaleString()}</td>
                      <td className="px-4 py-2 font-mono text-right">{v.baseline_index}</td>
                      <td className="px-4 py-2"><SB status={v.status} /></td>
                      <td className="px-4 py-2 text-content-secondary text-[10px] max-w-[200px] truncate">{v.commentary}</td>
                      <td className="px-4 py-2 text-[9px] text-brand-primary hover:underline font-semibold">{selRow?.id === v.id ? 'Close' : 'Details'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {selRow && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 animate-slide-up">
              <EvidencePanel linkedRecord={selRow.id} linkedType="metric_value" onUpload={fetchData} />
              <WorkflowPanel recordId={selRow.id} recordType="metric_value" currentStatus={selRow.status} onUpdate={(u) => { setSelRow(u); fetchData(); }} />
            </div>
          )}
        </div>
      )}

      {/* PROCUREMENT TAB */}
      {tab === 'procurement' && (
        <div className="space-y-4">
          <div className="bg-white border border-border-default rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border-default"><h3 className="font-heading font-semibold text-sm">Tender Register</h3></div>
            <div className="overflow-x-auto">
              <table data-testid="proc-table" className="w-full text-[12px]">
                <thead><tr className="bg-surface-muted">
                  {['Tender', 'Category', 'Value (PHP)', 'Supplier', 'Green', 'Evidence', 'Status'].map(h => <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-border-subtle">
                  {procurement.map((r, i) => (
                    <tr key={i} className="hover:bg-surface-muted/50">
                      <td className="px-4 py-2.5 font-mono text-[10px] text-brand-primary font-medium">{r.tender_id}</td>
                      <td className="px-4 py-2.5">{r.category}</td>
                      <td className="px-4 py-2.5 font-mono text-right">{r.estimated_value?.toLocaleString()}</td>
                      <td className="px-4 py-2.5 font-medium">{r.supplier}</td>
                      <td className="px-4 py-2.5"><SB status={r.green_criteria_included ? 'Approved' : 'Alert'} /></td>
                      <td className="px-4 py-2.5"><SB status={r.supplier_evidence_complete ? 'Complete' : 'Partial'} /></td>
                      <td className="px-4 py-2.5"><SB status={r.award_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white border border-border-default rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border-default"><h3 className="font-heading font-semibold text-sm">Supplier Registry</h3></div>
            <div className="overflow-x-auto">
              <table data-testid="sup-table" className="w-full text-[12px]">
                <thead><tr className="bg-surface-muted">
                  {['Supplier', 'Category', 'Sustainability', 'Certifications'].map(h => <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-border-subtle">
                  {suppliers.map((s, i) => (
                    <tr key={i} className="hover:bg-surface-muted/50">
                      <td className="px-4 py-2.5 font-medium">{s.name}</td>
                      <td className="px-4 py-2.5">{s.category}</td>
                      <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.sustainability_status === 'Certified' ? 'bg-status-on-track-bg text-status-on-track' : s.sustainability_status === 'Partial' ? 'bg-status-watch-bg text-status-watch' : 'bg-status-alert-bg text-status-alert'}`}>{s.sustainability_status}</span></td>
                      <td className="px-4 py-2.5"><div className="flex flex-wrap gap-1">{(s.certifications || []).map((c, j) => <span key={j} className="px-1.5 py-0.5 bg-brand-primary/5 text-brand-primary rounded text-[9px] font-medium">{c}</span>)}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* GOVERNANCE TAB */}
      {tab === 'governance' && (
        <div className="space-y-4">
          <div className="bg-white border border-border-default rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border-default"><h3 className="font-heading font-semibold text-sm">Resilience Register</h3></div>
            <div className="overflow-x-auto">
              <table data-testid="gov-table" className="w-full text-[12px]">
                <thead><tr className="bg-surface-muted">
                  {['Site', 'Hazard', 'Risk', 'Open', 'Overdue', 'Status'].map(h => <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-border-subtle">
                  {governance_records.map((r, i) => (
                    <tr key={i} className="hover:bg-surface-muted/50">
                      <td className="px-4 py-2.5 font-medium">{r.site_name}</td>
                      <td className="px-4 py-2.5 text-[10px]">{r.hazard_exposure}</td>
                      <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.risk_rating === 'High' ? 'bg-status-alert-bg text-status-alert' : r.risk_rating === 'Medium' ? 'bg-status-watch-bg text-status-watch' : 'bg-status-on-track-bg text-status-on-track'}`}>{r.risk_rating}</span></td>
                      <td className="px-4 py-2.5 font-mono">{r.open_actions}</td>
                      <td className="px-4 py-2.5 font-mono font-semibold" style={{ color: r.overdue_actions > 0 ? '#C6493A' : '#49564C' }}>{r.overdue_actions}</td>
                      <td className="px-4 py-2.5"><SB status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white border border-border-default rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border-default"><h3 className="font-heading font-semibold text-sm">Action Tracker</h3></div>
            <div className="overflow-x-auto">
              <table data-testid="act-table" className="w-full text-[12px]">
                <thead><tr className="bg-surface-muted">
                  {['Action', 'Site', 'Description', 'Owner', 'Due', 'Severity', 'Status'].map(h => <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-border-subtle">
                  {governance_actions.map((a, i) => (
                    <tr key={i} className="hover:bg-surface-muted/50">
                      <td className="px-4 py-2.5 font-mono text-[10px] text-brand-primary font-medium">{a.action_id}</td>
                      <td className="px-4 py-2.5 font-medium">{a.site_name}</td>
                      <td className="px-4 py-2.5 text-[10px] max-w-[200px]">{a.description}</td>
                      <td className="px-4 py-2.5">{a.owner}</td>
                      <td className="px-4 py-2.5 font-mono text-[10px]">{a.due_date}</td>
                      <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${a.severity === 'High' ? 'bg-status-alert-bg text-status-alert' : 'bg-status-watch-bg text-status-watch'}`}>{a.severity}</span></td>
                      <td className="px-4 py-2.5"><SB status={a.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* REPORTING TAB */}
      {tab === 'reporting' && (
        <div className="space-y-4">
          <div className="bg-white border border-border-default rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border-default"><h3 className="font-heading font-semibold text-sm">Reporting Cycles</h3></div>
            <div className="overflow-x-auto">
              <table data-testid="cyc-table" className="w-full text-[12px]">
                <thead><tr className="bg-surface-muted">
                  {['Cycle', 'Period', 'Submit By', 'Review By', 'Approve By', 'Status'].map(h => <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-border-subtle">
                  {cycles.map((c, i) => (
                    <tr key={i} className="hover:bg-surface-muted/50">
                      <td className="px-4 py-2.5 font-mono text-[10px] text-brand-primary font-medium">{c.cycle_id}</td>
                      <td className="px-4 py-2.5">{c.reporting_period}</td>
                      <td className="px-4 py-2.5 font-mono text-[10px]">{c.submission_deadline}</td>
                      <td className="px-4 py-2.5 font-mono text-[10px]">{c.review_deadline}</td>
                      <td className="px-4 py-2.5 font-mono text-[10px]">{c.approval_deadline}</td>
                      <td className="px-4 py-2.5"><SB status={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white border border-border-default rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border-default"><h3 className="font-heading font-semibold text-sm">Disclosure Tracker</h3></div>
            <div className="overflow-x-auto">
              <table data-testid="disc-table" className="w-full text-[12px]">
                <thead><tr className="bg-surface-muted">
                  {['Metric', 'Name', 'Owner', 'Submission', 'Evidence', 'Comment', 'Final'].map(h => <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-border-subtle">
                  {submissions.map((s, i) => (
                    <tr key={i} className="hover:bg-surface-muted/50">
                      <td className="px-4 py-2.5 font-mono text-[10px] text-brand-primary font-medium">{s.metric_id}</td>
                      <td className="px-4 py-2.5 font-medium">{s.metric_name}</td>
                      <td className="px-4 py-2.5">{s.owner}</td>
                      <td className="px-4 py-2.5"><SB status={s.submission_status} /></td>
                      <td className="px-4 py-2.5"><SB status={s.evidence_status} /></td>
                      <td className="px-4 py-2.5 text-[10px] text-content-secondary max-w-[150px] truncate">{s.reviewer_comment || '-'}</td>
                      <td className="px-4 py-2.5"><SB status={s.final_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* CURRENCY TAB */}
      {tab === 'currency' && (
        <div className="space-y-4">
          <div className="bg-white border border-border-default rounded-lg p-4">
            <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Currency Operations Sustainability Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {currency_metrics?.map((m, i) => (
                <div key={i} className="p-4 bg-surface-default rounded-lg border border-border-subtle text-center">
                  <p className="text-[10px] text-content-secondary font-medium">{m.metric}</p>
                  <p className="text-2xl font-heading font-bold text-brand-primary mt-2">{m.value}<span className="text-xs font-body font-normal text-content-disabled ml-1">{m.unit}</span></p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Entry Modal */}
      {showEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div data-testid="entry-modal" className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 animate-fade-in">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border-default">
              <h3 className="font-heading font-semibold text-base">New Metric Entry</h3>
              <button onClick={() => { setShowEntry(false); setEntryError(''); }}><X size={16} /></button>
            </div>
            <form onSubmit={handleEntry} className="p-5 space-y-3">
              {entryError && <div className="p-2.5 bg-status-alert-bg text-status-alert text-xs rounded-lg">{entryError}</div>}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[10px] font-semibold text-content-secondary uppercase mb-1">Metric</label><select disabled value={selMetric} className="w-full px-2.5 py-2 border border-border-default rounded-lg text-xs bg-surface-muted">{Object.entries(metricLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                <div><label className="block text-[10px] font-semibold text-content-secondary uppercase mb-1">Site *</label><select data-testid="e-site" value={entryForm.site_id} onChange={e => setEntryForm({ ...entryForm, site_id: e.target.value })} className="w-full px-2.5 py-2 border border-border-default rounded-lg text-xs bg-white" required><option value="">Select</option>{sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[10px] font-semibold text-content-secondary uppercase mb-1">Period *</label><input data-testid="e-period" type="month" value={entryForm.period} onChange={e => setEntryForm({ ...entryForm, period: e.target.value })} className="w-full px-2.5 py-2 border border-border-default rounded-lg text-xs" required /></div>
                <div><label className="block text-[10px] font-semibold text-content-secondary uppercase mb-1">Value *</label><input data-testid="e-value" type="number" step="0.01" min="0" value={entryForm.value} onChange={e => setEntryForm({ ...entryForm, value: e.target.value })} className="w-full px-2.5 py-2 border border-border-default rounded-lg text-xs" required /></div>
              </div>
              <div><label className="block text-[10px] font-semibold text-content-secondary uppercase mb-1">Commentary</label><textarea value={entryForm.commentary} onChange={e => setEntryForm({ ...entryForm, commentary: e.target.value })} className="w-full px-2.5 py-2 border border-border-default rounded-lg text-xs resize-none" rows={2} /></div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => { setShowEntry(false); setEntryError(''); }} className="flex-1 py-2 border border-border-default rounded-lg text-xs font-semibold hover:bg-surface-muted">Cancel</button>
                <button data-testid="e-submit" type="submit" className="flex-1 py-2 bg-brand-primary text-white rounded-lg text-xs font-semibold hover:bg-brand-primary-hover">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <DrillDownModal isOpen={!!drillKey} onClose={() => setDrillKey(null)} metricKey={drillKey} />
    </div>
  );
}
